import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff, Copy, Check, Lock } from "lucide-react";
import { toast } from "sonner";

const AUTO_HIDE_MS = 20_000;

/**
 * Restricted reveal-password button. Admin + finance only.
 * - Fetches the plaintext value on demand (audit-logged server-side)
 * - Auto-hides after 20 seconds
 * - Copy-to-clipboard supported
 * - Never renders anything for non-privileged roles
 */
export default function StatementPasswordReveal({ cardId, label = "Reveal password", size = "sm" }) {
    const { user } = useAuth();
    const [revealed, setRevealed] = useState(null);
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    if (!user || !["admin", "finance"].includes(user.role)) return null;

    const hide = () => {
        setRevealed(null);
        setCopied(false);
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const reveal = async () => {
        if (revealed) {
            hide();
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.get(`/cards/${cardId}/statement-password`);
            setRevealed(data);
            toast.success("Password revealed — auto-hides in 20s", { duration: 3000 });
            timerRef.current = setTimeout(hide, AUTO_HIDE_MS);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not reveal password";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        if (!revealed?.password) return;
        try {
            await navigator.clipboard.writeText(revealed.password);
            setCopied(true);
            toast.success("Password copied to clipboard", { duration: 2500 });
            setTimeout(() => setCopied(false), 2500);
        } catch (e) {
            toast.error("Could not copy — please copy manually");
        }
    };

    const btnSize = size === "xs" ? "text-[10.5px] px-2 py-1" : "text-xs px-2.5 py-1.5";

    if (!revealed) {
        return (
            <button
                type="button"
                onClick={reveal}
                disabled={busy}
                className={`inline-flex items-center gap-1.5 rounded-full border border-stone-300 text-stone-700 hover:text-stone-900 hover:border-stone-900 font-medium transition-colors ${btnSize}`}
                data-testid={`reveal-pw-${cardId}`}
                title="Reveal statement PDF password (audit-logged)"
            >
                <Eye size={12} />
                {busy ? "…" : label}
            </button>
        );
    }

    return (
        <div
            className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1"
            data-testid={`revealed-pw-${cardId}`}
        >
            <Lock size={11} className="text-amber-700 shrink-0" />
            <span className="font-mono-figure text-xs text-amber-900 select-all">
                {revealed.password}
            </span>
            <button
                type="button"
                onClick={copy}
                className="text-amber-800 hover:text-amber-900 transition-colors"
                aria-label="Copy password"
                data-testid={`copy-pw-${cardId}`}
            >
                {copied ? <Check size={12} /> : <Copy size={12} />}
            </button>
            <button
                type="button"
                onClick={hide}
                className="text-amber-800 hover:text-amber-900 transition-colors"
                aria-label="Hide password"
                data-testid={`hide-pw-${cardId}`}
            >
                <EyeOff size={12} />
            </button>
        </div>
    );
}
